import { pageTitle } from '@palaccount/config';
import { PAGE_SIZE_ALL, TRANSFER_MESSAGES, type StockTransferResponse } from '@palaccount/contracts';
import { AlertTriangle, ArrowRight, Loader2, Plus, Trash2, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { Ltr, Qty } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentToolbar } from '@/components/document-toolbar';
import { ReasonBox } from '@/components/reason-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  blockKey,
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from '@/features/sales/tracking-capture';
import { TrackingRows } from '@/features/sales/tracking-rows';
import { parseSerials } from '@/features/purchases/grn-rules';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useDocumentDate } from '@/lib/session';
import { formatQty } from '@/lib/format';

import { ItemCell } from './item-cell';
import { useItemPicker } from './item-picker';
import { trimZeros } from './conversion';
import { TransferStatusChip } from './stock-chips';
import { useStockLevels, useStockTransfer, useStockTransferMutations, useWarehouses } from './stock-api';
import { exceedsAvailable, pendingQty, toMilliQty } from './stock-format';

const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;

interface LineRow {
  key: number;
  /** The SERVER's line id once it exists — the receive payload keys on it. */
  id: string | null;
  itemId: string;
  qty: string;
  availableAtSource: string;
  /**
   * `N-4`, as the SERVER wrote it for this line. The picker below is the
   * first source (a draft row whose item was just changed has no server line
   * yet, and a saved row whose item was swapped has a stale one), but a
   * posted transfer can name an item the active catalogue no longer lists —
   * and a row that cannot say "kg" is the whole defect.
   */
  unitSymbol: string;
  unitPrecision: number;
  /** BR-MD-03-17: which units are travelling, typed while it is a draft. */
  tracking: CaptureText;
}

let nextKey = 1;
const blankLine = (): LineRow => ({
  key: nextKey++,
  id: null,
  itemId: '',
  qty: '',
  availableAtSource: '0',
  unitSymbol: '',
  unitPrecision: 0,
  tracking: {},
});

function fromTransfer(transfer: StockTransferResponse): LineRow[] {
  return transfer.lines.map((line) => ({
    key: nextKey++,
    id: line.id,
    itemId: line.itemId,
    qty: trimZeros(line.qty),
    availableAtSource: line.availableAtSource,
    unitSymbol: line.unitSymbol,
    unitPrecision: line.unitPrecision,
    tracking: textFromStored({ serialNos: line.serialNos, batchNo: line.batchNo }),
  }));
}

/** INV-07 §5.1 — Draft → In Transit → Completed; cancelled empties every dot. */
function Stepper({ status }: { status: StockTransferResponse['status'] }) {
  const { t } = useTranslation();
  const reached =
    status === 'cancelled' ? 0 : status === 'draft' ? 1 : status === 'in_transit' ? 2 : 3;
  const steps = ['trf.stepDraft', 'trf.stepInTransit', 'trf.stepCompleted'] as const;
  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm">
      {steps.map((step, index) => (
        <li key={step} className="flex items-center gap-2">
          <span
            className={cn(
              'size-3 rounded-full border-2',
              index < reached ? 'border-primary bg-primary' : 'border-muted-foreground/40',
            )}
            aria-hidden
          />
          <span className={cn(index < reached ? 'font-medium' : 'text-muted-foreground')}>
            {t(step)}
          </span>
          {index < steps.length - 1 ? (
            <ArrowRight className="size-3.5 text-muted-foreground rtl:rotate-180" aria-hidden />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * INV-07 §5 — the transfer. Instant completes both legs at once; two-step
 * ships (source down, value in 1133) and later receives (destination up).
 * Status moves ONLY through the workflow actions (BR-INV-07-19).
 *
 * Over-available WARNS and never blocks (BR-INV-07-14) — the negative-stock
 * rule bites at ship time. A blank received quantity means "in full"
 * (BR-INV-07-08); when partials are off and a line is short, plain Receive is
 * refused and the discrepancy path takes over, carrying the reason that will
 * be written on the adjustment (BR-INV-07-11/-12/-13).
 */
export function TransferEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { transferId } = useParams<{ transferId: string }>();
  const isNew = !transferId;

  const { data: transfer, isPending: loading } = useStockTransfer(transferId);
  const { data: warehouses } = useWarehouses({ status: 'active' });

  const mutations = useStockTransferMutations(transferId ?? '');

  const canCreate = useCan('inventory.create');
  const canEdit = useCan('inventory.edit');
  const canApprove = useCan('inventory.approve');

  const [transferDate, setTransferDate] = useDocumentDate();
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');

  /**
   * MD-08 field 4: a transfer whose two warehouses answer to different legal
   * entities is INTERCOMPANY. Computed from the loaded warehouse rows; null
   * when internal, incomplete, or same-entity.
   */
  const intercompany = useMemo(() => {
    const from = (warehouses ?? []).find((row) => row.id === fromWarehouseId);
    const to = (warehouses ?? []).find((row) => row.id === toWarehouseId);
    if (!from || !to || from.legalEntityId === to.legalEntityId) return null;
    return {
      from: from.legalEntityName ?? from.name,
      to: to.legalEntityName ?? to.name,
    };
  }, [warehouses, fromWarehouseId, toWarehouseId]);
  const [mode, setMode] = useState<'instant' | 'two_step'>('instant');
  const [allowPartial, setAllowPartial] = useState(true);
  const [requestedBy, setRequestedBy] = useState('');
  const [reference, setReference] = useState('');
  const [carrier, setCarrier] = useState('');
  const [driver, setDriver] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [expectedArrival, setExpectedArrival] = useState('');
  const [actualArrival, setActualArrival] = useState('');
  const [lines, setLines] = useState<LineRow[]>([blankLine()]);
  const [received, setReceived] = useState<Record<string, string>>({});
  /** Which units ARRIVED, per server line id — the receiver's half of the name. */
  const [receivedTracking, setReceivedTracking] = useState<Record<string, string>>({});
  const [discrepancyOpen, setDiscrepancyOpen] = useState(false);
  /**
   * GEN-59 (spec line 3806): the cancel asks through §8's typed-reason
   * `ReasonBox` — "Cancel must always ask for a reason, and must store that
   * reason on the record." The contract carries it (stock-transfers.ts cancel
   * body), the service writes `cancel_reason` + the audit-row reason
   * (20260903100000), and the cancelled notice reads it back. The undone
   * clause is in the prompt because cancelled is TERMINAL, established in the
   * service: `wrongState` 409 from completed/cancelled
   * (stock-transfers.service.ts cancel), no un-cancel route on the contract —
   * the returned stock is a new pair of movements, not an undo of the act.
   */
  const [cancelAsk, setCancelAsk] = useState(false);
  const [discrepancyReason, setDiscrepancyReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t(isNew ? 'trf.newTitle' : 'trf.editTitle'));
  }, [t, isNew]);

  useEffect(() => {
    if (!transfer) return;
    const stamp = `${transfer.id}:${transfer.updatedAt}:${transfer.status}`;
    if (loaded.current === stamp) return;
    loaded.current = stamp;
    setTransferDate(transfer.transferDate);
    setFromWarehouseId(transfer.fromWarehouseId);
    setToWarehouseId(transfer.toWarehouseId);
    setMode(transfer.mode);
    setAllowPartial(transfer.allowPartial);
    setRequestedBy(transfer.requestedBy);
    setReference(transfer.reference ?? '');
    setCarrier(transfer.carrier ?? '');
    setDriver(transfer.driver ?? '');
    setVehicleNo(transfer.vehicleNo ?? '');
    setExpectedArrival(transfer.expectedArrival ?? '');
    setActualArrival(transfer.actualArrival ?? '');
    setLines(transfer.lines.length > 0 ? fromTransfer(transfer) : [blankLine()]);
    setReceived(
      Object.fromEntries(
        transfer.lines.map((line) => [line.id, line.receivedQty === null ? '' : trimZeros(line.receivedQty)]),
      ),
    );
    // The shipped names, pre-filled: receiving is a storekeeper crossing off
    // what did NOT turn up, not typing four serials again from a packing list.
    setReceivedTracking(
      Object.fromEntries(
        transfer.lines.map((line) => [
          line.id,
          (line.receivedSerialNos ?? line.serialNos ?? []).join(', '),
        ]),
      ),
    );
    setDiscrepancyOpen(false);
    setDiscrepancyReason('');
    setError(null);
  }, [transfer]);

  const status = transfer?.status ?? 'draft';
  const isDraft = status === 'draft';
  const inTransit = status === 'in_transit';
  const editable = isDraft && canEdit && (isNew ? canCreate : true);

  // BR-INV-07-02 / BR-INV-04-05: only ACTIVE locations that allow transfers.
  const transferable = useMemo(
    () => (warehouses ?? []).filter((row) => row.allowTransfers),
    [warehouses],
  );
  /*
   * GEN-50 — searched, not listed, and narrowed by the SERVER's `stockable`
   * predicate. That predicate is BR-MD-03-07 in full: no service, and no
   * auto-deducting kit. This screen previously excluded only services, which
   * was the same rule written half way — an auto-deducting kit is assembled
   * when it is SOLD and never sits on a shelf, so there is no quantity of one
   * at a warehouse to move to another. INV-08's voucher already refused them
   * for exactly that reason.
   */
  const itemPicker = useItemPicker(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
    { status: 'active', stockable: true },
  );

  // Live availability at the source, so the §5.5 warning works while typing —
  // before the draft has ever been saved.
  // Every item at the source, not a page of them: an availability map with a
  // hole in it warns about nothing and says nothing about why.
  const { data: sourceLevels } = useStockLevels(
    fromWarehouseId ? { warehouseIds: [fromWarehouseId] } : {},
    1,
    PAGE_SIZE_ALL,
  );
  const availableAt = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of sourceLevels?.rows ?? []) {
      const here = row.warehouses.find((entry) => entry.warehouseId === fromWarehouseId);
      map.set(row.itemId, here?.available ?? row.available);
    }
    return map;
  }, [sourceLevels, fromWarehouseId]);

  const availableFor = (line: LineRow) =>
    availableAt.get(line.itemId) ?? line.availableAtSource ?? '0';

  /**
   * `N-4` — the unit every figure on this row is measured in. The catalogue
   * comes first because the picker can change the item under a saved row and
   * the server's copy would then be a lie; the server's copy is the fallback
   * because a completed transfer may name an item the ACTIVE catalogue no
   * longer carries, and "40" with no unit is what this rule exists to stop.
   *
   * There is only one unit to choose: `TransferLineInputSchema` takes an item
   * and a quantity, so a transfer line has no unit of its own to disagree
   * with the item's stock unit (unlike a sales line — `BR-MD-03-04d`).
   */
  const unitFor = (line: LineRow) => {
    const item = itemPicker.byId.get(line.itemId);
    return item
      ? { symbol: item.stockUnitSymbol, precision: item.stockUnitPrecision }
      : { symbol: line.unitSymbol, precision: line.unitPrecision };
  };

  /**
   * BR-MD-03-17 on the transfer grid — the same sub-table the invoice, the
   * delivery note and the goods receipt draw, because one rule met in one
   * shape is what stops a clerk learning it twice (OQ-05-17).
   *
   * `expandKits: false`: INV-07 moves the line's OWN item — it does not
   * assemble or break a kit — so the block asks about the thing that will
   * actually appear in a movement.
   */
  const blockFor = (itemId: string, qty: string) =>
    captureBlocks(
      itemPicker.byId.get(itemId) as TrackedItem | undefined,
      qty,
      { expandKits: false },
    )[0];

  const usedLines = lines.filter((line) => line.itemId !== '' && line.qty.trim() !== '');
  const anyOverAvailable = usedLines.some((line) =>
    exceedsAvailable(line.qty.trim(), availableFor(line)),
  );

  const totalQty = useMemo(() => {
    let total = 0n;
    for (const line of usedLines) {
      const scaled = toMilliQty(line.qty.trim());
      if (scaled !== null) total += scaled;
    }
    return total;
  }, [usedLines]);

  /**
   * BR-INV-07-11: partials off + any short line ⇒ the discrepancy path.
   *
   * And BR-INV-07-10 is the SAME leading clause read the other way — "when
   * partial transfers are ALLOWED, a shortfall is recorded as pending and PLAIN
   * RECEIVE STAYS AVAILABLE." The two rules govern one boolean, so the guard
   * that implements -11 is also the only thing that could break -10: drop the
   * `!allowPartial` term and a short line would take the discrepancy path in
   * both policies, which is -11 over-applied and -10 silently gone. Keeping the
   * pair on one expression is what makes them impossible to satisfy separately.
   * The "recorded as pending" half is the `pendingQty` call on each line below,
   * which is likewise computed only when partials are allowed.
   */
  const anyShort =
    !allowPartial &&
    (transfer?.lines ?? []).some((line) => {
      const typed = (received[line.id] ?? '').trim();
      if (typed === '') return false;
      const back = toMilliQty(typed);
      const out = toMilliQty(line.qty);
      return back !== null && out !== null && back < out;
    });

  /** #, item, available, qty, (received), (bin) — what a sub-row spans. */
  const columnCount = 4 + (inTransit ? 1 : 0) + (editable ? 1 : 0);

  const setLine = (key: number, patch: Partial<LineRow>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  const handleFailure = (
    thrown: unknown,
    permission: 'create' | 'edit' | 'delete' | 'approve',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) {
      denyPermission(`inventory.${permission}`);
    }
    // D46: the cause rides along — ship/receive/complete raise the stock
    // family (serial presence and whole-units, serviceNeverStocked,
    // itemInactive, insufficientStock) AND the warehouse wall (wh.errClosed
    // at the transfer route check), all structured, key+params.
    setError(
      localizeServerMessage(t, thrown instanceof Error ? thrown.message : String(thrown), thrown),
    );
  };

  const validate = (): string | null => {
    if (!fromWarehouseId || !toWarehouseId) return t('trf.errWarehouses');
    // BR-INV-07-01 — refused here, before the server has to say it.
    if (fromWarehouseId === toWarehouseId)
      return localizeServerMessage(t, TRANSFER_MESSAGES.sameLocation);
    if (requestedBy.trim() === '') return t('trf.errRequestedBy');
    if (usedLines.length === 0) return localizeServerMessage(t, TRANSFER_MESSAGES.noLines);
    for (const line of usedLines) {
      if (!QTY_RE.test(line.qty.trim()) || /^0+(\.0+)?$/.test(line.qty.trim())) {
        return t('trf.errQty');
      }
    }
    return null;
  };

  const requestBody = () => ({
    transferDate,
    fromWarehouseId,
    toWarehouseId,
    mode,
    allowPartial,
    requestedBy: requestedBy.trim(),
    reference: reference.trim() === '' ? null : reference.trim(),
    carrier: carrier.trim() === '' ? null : carrier.trim(),
    driver: driver.trim() === '' ? null : driver.trim(),
    vehicleNo: vehicleNo.trim() === '' ? null : vehicleNo.trim(),
    expectedArrival: expectedArrival === '' ? null : expectedArrival,
    lines: usedLines.map((line) => {
      const block = blockFor(line.itemId, line.qty.trim());
      const identified = block ? trackingPayload([block], line.tracking) : {};
      return {
        itemId: line.itemId,
        qty: line.qty.trim(),
        ...(identified.serialNos ? { serialNos: identified.serialNos } : {}),
        ...(identified.batchNo ? { batchNo: identified.batchNo } : {}),
      };
    }),
  });

  const onSave = async () => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        const created = await mutations.create.mutateAsync(requestBody());
        void navigate(`/inventory/transfers/${created.id}`, { replace: true });
      } else {
        await mutations.update.mutateAsync(requestBody());
        loaded.current = null;
      }
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'create' : 'edit');
    } finally {
      setBusy(false);
    }
  };

  /** In Transit: only the partial policy and the logistics are still open. */
  const onSaveInTransit = async () => {
    setBusy(true);
    setError(null);
    try {
      await mutations.update.mutateAsync({
        allowPartial,
        carrier: carrier.trim() === '' ? null : carrier.trim(),
        driver: driver.trim() === '' ? null : driver.trim(),
        vehicleNo: vehicleNo.trim() === '' ? null : vehicleNo.trim(),
        expectedArrival: expectedArrival === '' ? null : expectedArrival,
        actualArrival: actualArrival === '' ? null : actualArrival,
      });
      loaded.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'edit');
    } finally {
      setBusy(false);
    }
  };

  const run = async (action: () => Promise<unknown>, permission: 'edit' | 'approve') => {
    setBusy(true);
    setError(null);
    try {
      await action();
      loaded.current = null;
    } catch (thrown) {
      handleFailure(thrown, permission);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Blank received = in full, so only typed boxes travel (BR-INV-07-08) — and
   * a typed box carries the NAMES that came with it, because on a tracked line
   * a quantity alone cannot say which three of the four arrived.
   */
  const receiveLines = () =>
    (transfer?.lines ?? [])
      .filter((line) => (received[line.id] ?? '').trim() !== '')
      .map((line) => {
        const shipped = line.serialNos ?? [];
        const names = parseSerials(receivedTracking[line.id] ?? '');
        return {
          lineId: line.id,
          receivedQty: (received[line.id] ?? '').trim(),
          ...(shipped.length > 0 ? { receivedSerialNos: names } : {}),
        };
      });

  const onReceive = async (reason?: string) => {
    const payload = receiveLines();
    await run(
      () =>
        mutations.receive.mutateAsync({
          ...(payload.length > 0 ? { lines: payload } : {}),
          ...(reason ? { discrepancyReason: reason } : {}),
          ...(actualArrival === '' ? {} : { actualArrival }),
        }),
      'approve',
    );
  };

  const onDelete = async () => {
    setDeleting(false);
    setBusy(true);
    setError(null);
    try {
      await mutations.remove.mutateAsync();
      void navigate('/inventory/transfers');
    } catch (thrown) {
      handleFailure(thrown, 'delete');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  return (
    <div className="grid gap-4">
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t(isNew ? 'trf.newTitle' : 'trf.editTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {transfer ? <Ltr value={transfer.code} /> : t('trf.codeNew')}
        </span>
        <TransferStatusChip status={status} />
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. The
            in-transit Cancel is NOT one of them: it lives further down the
            screen beside the shipment panel, not in this row, and moving it
            here would move it for the user. */}
        <DocumentToolbar
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list wearing the
              // same word. The primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/inventory/transfers',
            },
            {
              // The document's question and the role's, kept apart (16.7).
              id: 'delete',
              row: 'delete',
              label: t('trf.deleteDraft'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && isDraft,
              gate: { permission: 'inventory.delete', reasonKey: 'perm.cannot.deleteTransfers' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isDraft,
              gate: {
                permission: isNew ? 'inventory.create' : 'inventory.edit',
                reasonKey: isNew ? 'perm.cannot.createTransfers' : 'perm.cannot.editTransfers',
              },
              busy,
              onAct: () => void onSave(),
            },
            {
              // The SECOND Save — the in-transit one, which writes the
              // shipment fields rather than the lines. A different act behind
              // the same word, and it never drew a spinner, so `disabled`.
              id: 'save-in-transit',
              row: 'save',
              label: t('common.save'),
              variant: 'outline',
              visible: inTransit,
              gate: { permission: 'inventory.edit', reasonKey: 'perm.cannot.editTransfers' },
              disabled: busy,
              onAct: () => void onSaveInTransit(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && isDraft ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('trf.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      <Stepper status={status} />

      {inTransit ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          {/*
            * NO UNIT HERE, and it is `N-14` that says so rather than an
            * omission. `inTransitQty` is Σ over the transfer's LINES, and one
            * transfer can carry 3 kg of sheet and 4 boxes of screws — the sum
            * `N-14` calls meaningless. It has no single unit to name and no
            * unit's decimals to honour, so it prints as the plain count of
            * units the banner's own sentence claims it is. INV-06's net card
            * is the same decision, and the sums that DO have one unit say so.
            */}
          {t('trf.inTransitBanner', { n: trimZeros(transfer?.inTransitQty ?? '0') })}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD. Rows cancelled
              before the reason existed keep the old sentence. */}
          {transfer?.cancelReason != null
            ? t('trf.cancelledReasonNotice', { reason: transfer.cancelReason })
            : t('trf.cancelledNotice')}
        </p>
      ) : null}
      {/* BR-INV-07-14 — a warning, never a block. */}
      {isDraft && anyOverAvailable ? (
        <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          {localizeServerMessage(t, TRANSFER_MESSAGES.overAvailable)}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* ── The details ────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label htmlFor="trf-date">{t('common.date')}</Label>
              <Input
                id="trf-date"
                type="date"
                dir="ltr"
                value={transferDate}
                disabled={!editable}
                onChange={(event) => setTransferDate(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="trf-from">{t('trf.fFrom')}</Label>
              <NativeSelect
                id="trf-from"
                value={fromWarehouseId}
                disabled={!editable}
                onChange={(event) => setFromWarehouseId(event.target.value)}
              >
                <option value="">{t('trf.pickWarehouse')}</option>
                {transferable.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="trf-to">{t('trf.fTo')}</Label>
              <NativeSelect
                id="trf-to"
                value={toWarehouseId}
                disabled={!editable}
                onChange={(event) => setToWarehouseId(event.target.value)}
              >
                <option value="">{t('trf.pickWarehouse')}</option>
                {transferable.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {/* MD-08 field 4's one stated behaviour: owning-entity difference
                "is what makes a stock transfer between entities an
                INTERCOMPANY movement rather than an internal one". A
                classification the reader should see; no posting attaches. */}
            {intercompany ? (
              <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground sm:col-span-2 lg:col-span-4">
                {t('trf.intercompany', {
                  from: intercompany.from,
                  to: intercompany.to,
                })}
              </p>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="trf-requested-by">{t('trf.fRequestedBy')}</Label>
              <Input
                id="trf-requested-by"
                value={requestedBy}
                disabled={!editable}
                onChange={(event) => setRequestedBy(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="trf-reference">{t('trf.fReference')}</Label>
              <Input
                id="trf-reference"
                value={reference}
                disabled={!editable}
                onChange={(event) => setReference(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">
                {t('trf.fMode')}
                {!isDraft ? (
                  <span className="font-normal text-muted-foreground"> {t('trf.modeLocked')}</span>
                ) : null}
              </span>
              {/*
                BR-INV-07-05 — "BOTH LOCKS must VISIBLY DISABLE their controls,
                not silently ignore clicks." The two locks are the ones directly
                above and below: BR-INV-07-03's on the mode, BR-INV-07-04's on
                the partial policy. The rule is not about whether the value can
                change — that is -03 and -04 — it is about HOW the refusal is
                delivered, and the prototype delivered it by dropping the click.
                `disabled` is the whole of it: the control greys, it is skipped
                by the keyboard, and assistive technology announces it as
                unavailable, so a user learns the field is locked from the field
                rather than from clicking it and watching nothing happen. The
                `modeLocked` note beside the label above is the "visibly" half
                in words. A handler that returned early instead would satisfy
                -03 and -04 exactly as well and break this one.
              */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={mode === 'instant' ? 'default' : 'outline'}
                  disabled={!editable}
                  onClick={() => setMode('instant')}
                >
                  ⚡ {t('trf.modeInstant')}
                </Button>
                <Button
                  type="button"
                  variant={mode === 'two_step' ? 'default' : 'outline'}
                  disabled={!editable}
                  onClick={() => setMode('two_step')}
                >
                  🚚 {t('trf.modeTwoStep')}
                </Button>
              </div>
            </div>
            {/* BR-INV-07-03/-04: still changeable while the van is on the road. */}
            <Label htmlFor="trf-partial" className="flex items-end gap-2 font-normal">
              <Checkbox
                id="trf-partial"
                checked={allowPartial}
                disabled={!(editable || (inTransit && canEdit))}
                onCheckedChange={(checked) => setAllowPartial(checked === true)}
              />
              {t('trf.fAllowPartial')}
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* ── The lines ──────────────────────────────────────────────────── */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead>{t('trf.colItem')}</TableHead>
                <TableHead className="w-32 text-end">{t('trf.colAvailable')}</TableHead>
                <TableHead className="w-28 text-end">{t('trf.colQtyLine')}</TableHead>
                {inTransit ? (
                  <TableHead className="w-32 text-end">{t('trf.colReceived')}</TableHead>
                ) : null}
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, index) => {
                const available = availableFor(line);
                const unit = unitFor(line);
                const over = line.qty.trim() !== '' && exceedsAvailable(line.qty.trim(), available);
                const typedReceived = line.id ? (received[line.id] ?? '') : '';
                const pending = allowPartial ? pendingQty(line.qty, typedReceived) : null;
                // While the transfer is a draft the block asks what is
                // LEAVING; once it is in transit it asks what ARRIVED, sized
                // to the received quantity, and the box comes pre-filled with
                // what was sent.
                const stored = transfer?.lines.find((row) => row.id === line.id);
                const shipping = blockFor(line.itemId, line.qty.trim());
                const arriving = inTransit
                  ? blockFor(line.itemId, typedReceived.trim() === '' ? line.qty : typedReceived)
                  : undefined;
                return (
                  <Fragment key={line.key}>
                  <TableRow
                    className={cn(
                      'odd:bg-muted/30',
                      over && 'bg-amber-50 dark:bg-amber-950/30',
                    )}
                  >
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <ItemCell
                        label={t('trf.cellItem', { line: index + 1 })}
                        clearLabel={t('trf.pickItem')}
                        picker={itemPicker}
                        value={line.itemId}
                        disabled={!editable}
                        onPick={(itemId) => setLine(line.key, { itemId })}
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      <span className="inline-flex items-center gap-1.5">
                        {over ? (
                          <AlertTriangle
                            className="size-4 text-amber-700 dark:text-amber-400"
                            aria-hidden
                          />
                        ) : null}
                        {/* `N-4` — §5.5's availability figure is a display,
                            and a bare "40" in a column whose rows can be
                            kilograms and boxes is what `N-14` forbids. */}
                        <Qty value={available} precision={unit.precision} unit={unit.symbol} />
                      </span>
                    </TableCell>
                    {/*
                      * `N-4` — once the transfer has shipped the quantity is no
                      * longer something being typed, it is the RECORD of what
                      * left, and the rule's exception for "what a box holds
                      * mid-edit" stops applying with it. A greyed-out box
                      * reading "40" is a display that happens to be shaped like
                      * an input, so past Draft it becomes one: the decimals its
                      * unit allows, and the unit, in one island.
                      */}
                    <TableCell className={editable ? undefined : 'text-end'}>
                      {editable ? (
                        <Input
                          aria-label={t('trf.cellQty', { line: index + 1 })}
                          dir="ltr"
                          inputMode="decimal"
                          className="h-9 text-end font-mono"
                          value={line.qty}
                          onChange={(event) => setLine(line.key, { qty: event.target.value })}
                        />
                      ) : line.qty.trim() === '' ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Qty value={line.qty} precision={unit.precision} unit={unit.symbol} />
                      )}
                    </TableCell>
                    {inTransit ? (
                      <TableCell>
                        <Input
                          aria-label={t('trf.cellReceived', { line: index + 1 })}
                          dir="ltr"
                          inputMode="decimal"
                          // Blank = received in full (BR-INV-07-08).
                          placeholder={trimZeros(line.qty)}
                          className="h-9 text-end font-mono"
                          value={typedReceived}
                          disabled={!canApprove}
                          onChange={(event) =>
                            line.id
                              ? setReceived((current) => ({
                                  ...current,
                                  [line.id!]: event.target.value,
                                }))
                              : undefined
                          }
                        />
                        {pending ? (
                          <p className="mt-1 text-end text-xs text-amber-700 dark:text-amber-400">
                            {/* The decimals, not a second symbol: this note
                                sits one cell along from the quantity that
                                already prints the unit, on the same row and
                                in the same unit by construction — INV-06's
                                balance column, exactly. */}
                            {t('trf.pending', { n: formatQty(pending, unit.precision) })}
                          </p>
                        ) : null}
                      </TableCell>
                    ) : null}
                    {editable ? (
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('sales.removeLine', { line: index + 1 })}
                          onClick={() =>
                            setLines((current) => {
                              const rest = current.filter((row) => row.key !== line.key);
                              return rest.length === 0 ? [blankLine()] : rest;
                            })
                          }
                          className="flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                  {/* OQ-05-17's sub-table: the units, under the line moving them. */}
                  {isDraft && shipping ? (
                    <TrackingRows
                      blocks={[shipping]}
                      text={line.tracking}
                      onChange={(key, value) =>
                        setLine(line.key, { tracking: { ...line.tracking, [key]: value } })
                      }
                      disabled={!editable}
                      columns={columnCount}
                      line={index + 1}
                    />
                  ) : null}
                  {inTransit && arriving && line.id && (stored?.serialNos ?? []).length > 0 ? (
                    <TrackingRows
                      blocks={[{ ...arriving, name: t('trf.arrivedOf', { name: arriving.name }) }]}
                      text={{ [blockKey(arriving)]: receivedTracking[line.id] ?? '' }}
                      onChange={(_key, value) =>
                        setReceivedTracking((current) => ({ ...current, [line.id!]: value }))
                      }
                      disabled={!canApprove}
                      columns={columnCount}
                      line={index + 1}
                    />
                  ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3}>
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-dashed"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus aria-hidden /> {t('trf.addLine')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('trf.colQty')}
                  </span>{' '}
                  {/* Σ across ITEMS — `N-14`'s "adding 3 kg to 4 boxes is
                      meaningless". No unit to write and no precision to
                      apply; the footer counts, it does not measure. */}
                  <Ltr
                    value={trimZeros(
                      `${totalQty / 1000n}.${(totalQty % 1000n).toString().padStart(3, '0')}`,
                    )}
                    className="font-mono font-semibold"
                  />
                </TableCell>
                {inTransit ? <TableCell /> : null}
                {editable ? <TableCell /> : null}
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {/* ── Shipping & logistics ───────────────────────────────────────── */}
      <details className="rounded-xl border">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
          {t('trf.secShipping')}{' '}
          <span className="font-normal text-muted-foreground">— {t('trf.secShippingHint')}</span>
        </summary>
        <div className="grid gap-4 border-t p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="grid gap-1.5">
            <Label htmlFor="trf-carrier">{t('trf.fCarrier')}</Label>
            <Input
              id="trf-carrier"
              value={carrier}
              disabled={!(editable || (inTransit && canEdit))}
              onChange={(event) => setCarrier(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="trf-driver">{t('trf.fDriver')}</Label>
            <Input
              id="trf-driver"
              value={driver}
              disabled={!(editable || (inTransit && canEdit))}
              onChange={(event) => setDriver(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="trf-vehicle">{t('trf.fVehicle')}</Label>
            <Input
              id="trf-vehicle"
              value={vehicleNo}
              disabled={!(editable || (inTransit && canEdit))}
              onChange={(event) => setVehicleNo(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="trf-expected">{t('trf.fExpectedArrival')}</Label>
            <Input
              id="trf-expected"
              type="date"
              dir="ltr"
              value={expectedArrival}
              disabled={!(editable || (inTransit && canEdit))}
              onChange={(event) => setExpectedArrival(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="trf-actual">{t('trf.fActualArrival')}</Label>
            <Input
              id="trf-actual"
              type="date"
              dir="ltr"
              value={actualArrival}
              disabled={!(inTransit && canEdit)}
              onChange={(event) => setActualArrival(event.target.value)}
            />
          </div>
        </div>
      </details>

      {/* ── The workflow ───────────────────────────────────────────────── */}
      {!isNew && status !== 'completed' && status !== 'cancelled' ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('trf.secWorkflow')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {isDraft && mode === 'instant' ? (
                <Gated permission="inventory.approve" reasonKey="perm.cannot.moveStock">
                  <Button
                    disabled={busy}
                    onClick={() => void run(() => mutations.complete.mutateAsync(), 'approve')}
                  >
                    {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
                    {t('trf.complete')}
                  </Button>
                </Gated>
              ) : null}
              {isDraft && mode === 'two_step' ? (
                <Gated permission="inventory.approve" reasonKey="perm.cannot.moveStock">
                  <Button
                    disabled={busy}
                    onClick={() => void run(() => mutations.ship.mutateAsync(), 'approve')}
                  >
                    {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
                    {t('trf.ship')}
                  </Button>
                </Gated>
              ) : null}
              {inTransit ? (
                <Gated permission="inventory.approve" reasonKey="perm.cannot.moveStock">
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={busy || anyShort}
                    onClick={() => void onReceive()}
                  >
                    {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
                    {t('trf.receive')}
                  </Button>
                </Gated>
              ) : null}
              {inTransit && anyShort ? (
                <Gated permission="inventory.approve" reasonKey="perm.cannot.moveStock">
                  <Button
                    variant="outline"
                    className="border-amber-400 text-amber-700 dark:text-amber-300"
                    onClick={() => setDiscrepancyOpen(true)}
                  >
                    {t('trf.receiveDiscrepancy')}
                  </Button>
                </Gated>
              ) : null}
              {isDraft || inTransit ? (
                <>
                  <Gated
                    permission="inventory.edit"
                    reasonKey="perm.cannot.cancelTransfers"
                    className="ms-auto"
                  >
                    {/* GEN-208: the click OPENS the question and does nothing
                        else. The write happens on the dialog's own verb, with
                        GEN-59's typed reason, never here. */}
                    <Button
                      variant="outline"
                      className="ms-auto border-destructive/50 text-destructive"
                      disabled={busy}
                      onClick={() => setCancelAsk(true)}
                    >
                      {t('trf.cancelTransfer')}
                    </Button>
                  </Gated>
                  {/* Inside the same guard as the button, so the dialog cannot
                      outlive the state that allows the cancel. */}
                  <ReasonBox
                    open={cancelAsk}
                    title={t('trf.confirmCancelTitle')}
                    prompt={t('trf.confirmCancelBody')}
                    reasonLabel={t('gen72.cancelReasonLabel')}
                    confirmLabel={t('gen72.confirmCancel')}
                    busy={busy}
                    onConfirm={(reason) => {
                      setCancelAsk(false);
                      void run(() => mutations.cancel.mutateAsync(reason), 'edit');
                    }}
                    onCancel={() => setCancelAsk(false)}
                  />
                </>
              ) : null}
            </div>
            {/* §5.6 — plain Receive is refused, and says which door to use. */}
            {inTransit && anyShort ? (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {localizeServerMessage(t, TRANSFER_MESSAGES.useDiscrepancy)}
              </p>
            ) : null}
            {discrepancyOpen ? (
              <div className="grid gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <p>{t('trf.discrepancyIntro')}</p>
                <div className="grid gap-1.5">
                  <Label htmlFor="trf-discrepancy">{t('trf.fDiscrepancyReason')}</Label>
                  <Textarea
                    id="trf-discrepancy"
                    maxLength={500}
                    value={discrepancyReason}
                    onChange={(event) => setDiscrepancyReason(event.target.value)}
                  />
                  {discrepancyReason.trim() === '' ? (
                    <p className="text-xs">
                      {localizeServerMessage(t, TRANSFER_MESSAGES.discrepancyReason)}
                    </p>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    disabled={discrepancyReason.trim() === '' || busy}
                    onClick={() => void onReceive(discrepancyReason.trim())}
                  >
                    {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
                    {t('trf.logAndComplete')}
                  </Button>
                  <Button variant="outline" onClick={() => setDiscrepancyOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
